import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageContainer } from '@/components/layout/PageContainer';
import { restaurantService } from '@/services/restaurant';
import type { RestaurantWithProduct } from '@/types';
import { useLocationStore } from '@/store/location';
import { useToast } from '@/components/common/Toast';
import { useFilterStore } from '@/store/filter';
import { MobileMapView } from '@/components/mobile/MobileMapView';
import { favoritesService } from '@/services/favorites';
import { useAuth } from '@/contexts/AuthContext';
import { useStoreFiltering } from '@/hooks/useStoreFiltering';
import { HomeSEO } from '@/components/seo/SEO';
import { SCHEMA_TEMPLATES } from '@/constants/seo';

// New components
import { HomeHeader } from '@/components/home/HomeHeader';
import { OrderTypeSelector } from '@/components/home/OrderTypeSelector';
import { BannerCarousel } from '@/components/home/BannerCarousel';
import { ViewToggle } from '@/components/home/ViewToggle';
import { StoreFilter } from '@/components/home/StoreFilter';
import { StoreList } from '@/components/home/StoreList';
import { ScrollToTopButton } from '@/components/home/ScrollToTopButton';
import { Footer } from '@/components/common/Footer';


export function MobileHomePage() {
  const navigate = useNavigate();
  const { currentLocation, checkAuthAndLoad } = useLocationStore();
  const { showToast, ToastContainer } = useToast();
  const { selectedStoreTypes, showOpenOnly, showClosedOnly } = useFilterStore();
  const { user, isLoading: authLoading } = useAuth();
  
  // 핵심 상태만 유지
  const [isMapView, setIsMapView] = useState(false);
  const [restaurants, setRestaurants] = useState<RestaurantWithProduct[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedDiscountType, setSelectedDiscountType] = useState<'DEADLINE_DISCOUNT' | 'RESERVATION_DISCOUNT' | 'REGULAR_ORDER'>(() => {
    const lastType = localStorage.getItem('lastHomeOrderType') as 'DEADLINE_DISCOUNT' | 'RESERVATION_DISCOUNT' | 'REGULAR_ORDER' | null;
    return lastType || 'REGULAR_ORDER';
  });
  const [sortOrder, setSortOrder] = useState<'newest' | 'distance'>('newest');
  const [favoriteStores, setFavoriteStores] = useState<Set<string>>(new Set());
  
  // 사용자 로그인 상태 변경 시 주소 데이터 다시 로드 (AuthContext 로딩 완료 후에만)
  useEffect(() => {
    if (!authLoading && user) {
      checkAuthAndLoad();
    }
  }, [user, authLoading, checkAuthAndLoad]);

  // 즐겨찾기 상태 로드
  useEffect(() => {
    const loadFavorites = async () => {
      try {
        const result = await favoritesService.getUserFavorites(user?.id || null);
        if (result.success && result.data) {
          setFavoriteStores(new Set(result.data));
        }
      } catch (error) {
        console.error('Failed to load favorites:', error);
      }
    };

    loadFavorites();
  }, [user]);


  // AuthContext 로딩 완료 후에만 매장 데이터 가져오기
  useEffect(() => {
    const fetchRestaurants = async () => {
      if (authLoading) return; // AuthContext 로딩 중이면 대기
      
      try {
        setIsLoading(true);
        
        // Get restaurants for the selected discount type with business logic applied
        const result = await restaurantService.getRestaurants({ 
          withProducts: true,
          offerType: selectedDiscountType // Add offer type to apply business logic
        });
        
        
        if (result.success && result.data) {
          
          // RPC function already filters restaurants with products, no need for additional filtering
          // Just map the data with proper typing while preserving operating hours
          const restaurantsWithProducts: RestaurantWithProduct[] = result.data.map(restaurant => ({
            ...restaurant,
            // Ensure proper typing
            store_type: (restaurant.store_type as 'korean' | 'chinese' | 'japanese' | 'western' | 'cafe' | 'lunchbox' | 'kimbap' | 'salad' | 'bakery' | 'buffet' | 'etc') || 'korean',
            bestmenu: restaurant.bestmenu || '',
            pickup_instructions: restaurant.pickup_instructions || '',
            latitude: restaurant.latitude || 0,
            longitude: restaurant.longitude || 0,
            // CRITICAL: Preserve operating hours data from restaurant.ts
            operating_hours: (restaurant as any).operating_hours,
            todayOperatingHours: (restaurant as any).todayOperatingHours,
            // Products might not be included but that's OK - RPC ensures only restaurants with products are returned
            products: restaurant.products?.map(product => ({
              ...product,
              is_available: (product as any).isAvailable ?? (product as any).is_available ?? true
            })) || []
          }));
          setRestaurants(restaurantsWithProducts);
        } else {
          console.error('Failed to fetch restaurants:', result.error);
        }
      } catch (error) {
        console.error('Failed to fetch restaurants:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchRestaurants();
  }, [authLoading, selectedDiscountType]); // Add selectedDiscountType as dependency

  // 탭 변경 시 로컬 스토리지에 저장
  useEffect(() => {
    localStorage.setItem('lastHomeOrderType', selectedDiscountType);
  }, [selectedDiscountType]);


  // 매장 필터링 및 정렬 - 커스텀 훅 사용
  const filteredAndSortedRestaurants = useStoreFiltering(
    restaurants,
    selectedDiscountType,
    sortOrder,
    { selectedStoreTypes, showOpenOnly, showClosedOnly },
    currentLocation?.coordinate
  ).map(restaurant => ({
    ...restaurant,
    pickup_instructions: restaurant.pickup_instructions || '픽업 정보 없음',
    // CRITICAL: Preserve operating hours data
    operating_hours: restaurant.operating_hours,
    todayOperatingHours: restaurant.todayOperatingHours
  })) as RestaurantWithProduct[];


  // 핸들러 함수들
  const handleSortToggle = () => {
    const newSortOrder = sortOrder === 'newest' ? 'distance' : 'newest';
    setSortOrder(newSortOrder);
  };

  const handleAddressButtonClick = () => {
    navigate('/address-select');
  };

  const handleStoreCardClick = (storeId: string) => {
    navigate(`/store/${storeId}?orderType=${selectedDiscountType}`);
  };

  const handleFavoriteToggle = async (storeId: string) => {
    try {
      const result = await favoritesService.toggleFavorite(user?.id || null, storeId);
      
      if (result.success) {
        setFavoriteStores(prev => {
          const newFavorites = new Set(prev);
          if (newFavorites.has(storeId)) {
            newFavorites.delete(storeId);
            showToast('즐겨찾기에서 제거되었습니다');
          } else {
            newFavorites.add(storeId);
            showToast('즐겨찾기에 추가되었습니다');
          }
          return newFavorites;
        });
      } else {
        showToast(result.error || '즐겨찾기 처리에 실패했습니다');
      }
    } catch (error) {
      console.error('Failed to toggle favorite:', error);
      showToast('즐겨찾기 처리에 실패했습니다');
    }
  };


  if (authLoading || isLoading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mx-auto mb-4"></div>
          <p className="text-gray-600">
            {authLoading ? "로그인 상태 확인 중..." : "매장 정보를 불러오는 중..."}
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <HomeSEO 
        structuredData={[
          SCHEMA_TEMPLATES.ORGANIZATION,
          SCHEMA_TEMPLATES.WEB_APPLICATION
        ]}
      />
      <div className="flex flex-col h-screen bg-[#F5F5F5]">
        {/* 스크롤 가능한 메인 콘텐츠 영역 */}
        <main className="flex-grow overflow-y-auto">
          <PageContainer hasBottomNav={true}>
          
          {/* 흰색 배경 영역 (주소 ~ 광고 배너까지) */}
          <div className="bg-white">
            <HomeHeader onAddressClick={handleAddressButtonClick} />
            <OrderTypeSelector 
              selectedOrderType={selectedDiscountType}
              onOrderTypeChange={setSelectedDiscountType}
              restaurants={restaurants}
            />
            <BannerCarousel />
          </div>

          <ViewToggle 
            isMapView={isMapView} 
            onToggle={setIsMapView} 
          />

          {/* 지도 뷰 또는 리스트 뷰 */}
          {isMapView ? (
            <div className="flex-grow" style={{ 
              marginLeft: '-16px', 
              marginRight: '-16px',
              marginTop: '0px',
              height: 'calc(100vh - 160px)'
            }}>
              <MobileMapView restaurants={filteredAndSortedRestaurants} selectedOrderType={selectedDiscountType} />
            </div>
          ) : (
            <>
              <StoreFilter 
                sortOrder={sortOrder}
                onSortToggle={handleSortToggle}
              />
              <StoreList 
                restaurants={filteredAndSortedRestaurants}
                selectedOrderType={selectedDiscountType}
                favoriteStores={favoriteStores}
                onStoreCardClick={handleStoreCardClick}
                onFavoriteToggle={handleFavoriteToggle}
              />
            </>
          )}
          
          {/* Footer - 항상 표시 */}
          <Footer />
        
        </PageContainer>
      </main>
      
      <ScrollToTopButton />
      <ToastContainer />
    </div>
    </>
  );
}
